import { KalturaSubscriptionDependencySet } from '@kaltura-ott/ott-client-types/api/types/KalturaSubscriptionDependencySet';
import { KuxSubscription, subscriptionsGetSets, tvpilGetLocalCache } from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import { featureEnabler } from '../../utils/feature-enabler';
import type { Subscription } from '../entities/Subscription';
import type { TransactionStore } from '../transaction/transaction-store';

const SUBSCRIPTIONS_TTL = 10 * 24 * 60 * 60 * 1000;

export enum SubscriptionType {
  addonForPurchasedBase = 'addonForPurchasedBase',
  base = 'base',
  addonForOtherBase = 'addonForOtherBase',
}

@singleton()
export class SubscriptionsRepository {
  @observable sets?: KalturaSubscriptionDependencySet[];

  private get transactionStore() {
    return container.resolve<TransactionStore>('transactionStore');
  }

  @computed get purchasedBasePlan(): Subscription {
    return this.transactionStore.plans[0];
  }

  async loadSubscriptionSets() {
    const cache = tvpilGetLocalCache();

    const cachedSets = (await cache.getItem('subscriptions_sets')) as KalturaSubscriptionDependencySet[];

    if (cachedSets) {
      this.sets = cachedSets;
      return;
    }

    const subscriptionSetsTtl = featureEnabler.variation('SUBSCRIPTION_SETS_TTL', SUBSCRIPTIONS_TTL);

    this.sets = await lastValueFrom(subscriptionsGetSets());

    cache.setItem('subscriptions_sets', this.sets, subscriptionSetsTtl);
  }

  availableAddonIds(id: string): string[] | undefined {
    return this.sets?.find((set) => set.baseSubscriptionId.toString() === id)?.subscriptionIds.split(',');
  }

  getPlanForAddon(id: string) {
    const planId = this.sets?.find((set) => set.subscriptionIds.includes(id))?.baseSubscriptionId;

    return this.transactionStore.additionalBasePlans.find((plan) => plan.data.id === planId?.toString());
  }

  availableAddonSubscriptionsForPurchasedBase(subscriptions: KuxSubscription[]) {
    if (!this.purchasedBasePlan) {
      return;
    }

    const availableAddonIds = this.availableAddonIds(this.purchasedBasePlan.data.id);

    if (!availableAddonIds) {
      return;
    }

    return subscriptions.filter(
      (subscription) => subscription.dependencyType === 'ADDON' && availableAddonIds.includes(subscription.id),
    );
  }

  availableBaseSubscriptions(subscriptions: KuxSubscription[]) {
    const base = subscriptions.filter(
      (subscription) =>
        subscription.dependencyType === 'BASE' &&
        subscription.price.amount > (this.purchasedBasePlan?.price.amount || 0),
    );

    return base.sort((a, b) => a.price.amount - b.price.amount);
  }

  availableAddonSubscriptionsForOtherBase(subscriptions: KuxSubscription[]) {
    const availableAddonIds = this.purchasedBasePlan
      ? this.availableAddonIds(this.purchasedBasePlan.data.id)
      : undefined;

    return subscriptions.filter(
      (subscription) =>
        subscription.dependencyType === 'ADDON' && (!availableAddonIds || !availableAddonIds.includes(subscription.id)),
    );
  }
}
